import { useEffect, useMemo, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Triangulation (level 45) — the deduction rung. A ping from one of your footholds reports the
// DIRECTION traffic to the hidden host leaves by, so following the arrow is trivial and the whole
// question is which foothold to spend a ping on.
//
// The surviving candidates come from the server, already worked out. That is not doing the puzzle for
// the player: it is exactly what their own pings have told them, and keeping the narrowing rule in one
// place stops a client-side copy of it drifting from the grader — which has happened on this ladder
// before. What the payload never contains is the target itself.

interface Host {
  id: number;
  name: string;
  links: number[];
}

interface Ping {
  from: number;
  via: number;
}

interface Challenge {
  started: boolean;
  hostCount: number;
  vantageCount: number;
  pingsLeft: number;
  pingsGranted: number;
  timeLimitSec: number;
  readingAllowanceSec: number;
  /** Which host to hang the indented map from — one of your own footholds. */
  displayRoot: number;
  hosts: Host[];
  vantages: number[];
  pings: Ping[];
  candidates: number[];
}

export default function TriangulationView({
  challenge, onAct, onSubmit, setPendingSubmission, busy,
}: MinigameViewProps<Challenge, Challenge>) {
  const [live, setLive] = useState<Challenge | null>(challenge ?? null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => { setLive(challenge ?? null); setNote(null); }, [challenge]);

  const hosts = useMemo(() => live?.hosts ?? [], [live]);
  const vantages = useMemo(() => new Set(live?.vantages ?? []), [live]);
  const candidates = useMemo(() => live?.candidates ?? [], [live]);
  const candidateSet = useMemo(() => new Set(candidates), [candidates]);
  const started = live?.started ?? false;

  // Only one candidate left means it is pinned down; the server refuses a name before that, so the
  // button is only offered when the deduction is actually complete.
  const pinned = candidates.length === 1 ? candidates[0] : null;

  useEffect(() => {
    setPendingSubmission(pinned === null ? {} : { host: pinned });
  }, [pinned, setPendingSubmission]);

  const send = async (action: unknown) => {
    setNote(null);
    const next = await onAct(action);
    if (next) setLive(next);
  };

  // Depth-first from the display root: the network is a tree, so the indentation IS the topology.
  const ordered = useMemo(() => {
    if (hosts.length === 0) return [] as { host: Host; depth: number }[];
    const byId = new Map(hosts.map(h => [h.id, h]));
    const out: { host: Host; depth: number }[] = [];
    const walk = (id: number, from: number, depth: number) => {
      const h = byId.get(id);
      if (!h) return;
      out.push({ host: h, depth });
      for (const l of h.links) if (l !== from) walk(l, id, depth + 1);
    };
    walk(live?.displayRoot ?? 0, -1, 0);
    return out;
  }, [hosts, live]);

  const nameOf = (id: number) => hosts.find(h => h.id === id)?.name ?? '?';
  const row: React.CSSProperties = { fontFamily: 'monospace', fontSize: '0.8rem' };

  if (!started) {
    return (
      <div style={{ fontFamily: 'monospace' }}>
        <div style={{ border: '1px solid #232833', borderRadius: 4, padding: '0.9rem', background: '#0d1117' }}>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            One host on a network of <strong>{live?.hostCount ?? 0}</strong> is running the thing you are
            looking for, and you do not know which. You hold a foothold on{' '}
            <strong>{live?.vantageCount ?? 0}</strong> of them.
          </p>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            A <strong>ping</strong> from one of your footholds does not tell you how far away the target
            is — it tells you <strong>which way</strong> traffic to it leaves that host. Every route
            through this network is unique, so that answer puts the target somewhere in exactly one
            branch, and everything in the others is ruled out.
          </p>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            You have <strong>{live?.pingsGranted ?? 0} ping{(live?.pingsGranted ?? 0) === 1 ? '' : 's'}</strong>,
            which is fewer than your footholds — so <em>which</em> ones you spend them on is the whole
            job. A ping from the wrong place narrows almost nothing.
          </p>
          <p style={{ ...row, color: '#60a5fa', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            You can only name the host once your pings have left <strong>exactly one</strong> that fits.
            Narrowing it down to a shortlist and picking one is not an option — this is about locating
            it, not guessing. The board is always solvable with the pings you have: there is a sequence
            that pins it down for certain, whatever the answer turns out to be.
          </p>
          <p style={{ ...row, color: 'var(--text-muted)', margin: '0 0 0.8rem' }}>
            The map arrives when you start the sweep. The clock is already running — about{' '}
            {live?.readingAllowanceSec ?? 30}s of it is set aside for reading this.
          </p>
          <button
            className="primary"
            onClick={() => void send({ kind: 'begin' })}
            disabled={busy}
            style={{ fontSize: '0.85rem', padding: '0.4rem 1rem' }}
          >
            Start the sweep
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'monospace' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
        border: '1px solid var(--border-color)', borderRadius: 4, padding: '0.5rem 0.7rem',
        marginBottom: '0.6rem', background: '#0d1117',
      }}>
        <span style={{ ...row, minWidth: '7rem' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>PINGS </span>
          <strong style={{ color: (live?.pingsLeft ?? 0) > 0 ? '#cbd5e1' : '#f87171' }}>{live?.pingsLeft ?? 0}</strong>
          <span style={{ color: '#4b5563' }}> / {live?.pingsGranted ?? 0}</span>
        </span>
        <span style={{ ...row, minWidth: '10rem' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>STILL POSSIBLE </span>
          <strong style={{ color: candidates.length === 1 ? '#4ade80' : '#cbd5e1' }}>{candidates.length}</strong>
          <span style={{ color: '#4b5563' }}> of {(live?.hostCount ?? 0) - (live?.vantageCount ?? 0)}</span>
        </span>
        <span style={{ ...row, fontSize: '0.72rem', color: '#6b7280' }}>
          ◆ your foothold · ○ still possible · · ruled out
        </span>
      </div>

      {/* What the pings have said. A fixed slot per granted ping, so the map never shifts as they land. */}
      <div style={{
        border: '1px solid #232833', borderRadius: 4, padding: '0.4rem 0.7rem', marginBottom: '0.6rem',
        background: '#0d1117', minHeight: `${1.3 * Math.max(1, live?.pingsGranted ?? 1) + 0.4}rem`,
      }}>
        {(live?.pings ?? []).map((p, i) => (
          <div key={i} style={{ ...row, fontSize: '0.75rem', color: '#60a5fa', lineHeight: 1.3 }}>
            {nameOf(p.from)} → traffic leaves via <strong>{nameOf(p.via)}</strong>
          </div>
        ))}
        {(live?.pings.length ?? 0) === 0 && (
          <div style={{ ...row, fontSize: '0.75rem', color: '#4b5563', lineHeight: 1.3 }}>
            no pings yet — pick a foothold that splits the possibilities most evenly
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gap: '0.1rem', marginBottom: '0.6rem' }}>
        {ordered.map(({ host, depth }) => {
          const isVantage = vantages.has(host.id);
          const isCandidate = candidateSet.has(host.id);
          return (
            <div key={host.id} style={{
              ...row, display: 'flex', alignItems: 'center', gap: '0.5rem',
              paddingLeft: `${depth * 1.05}rem`,
              opacity: !isVantage && !isCandidate ? 0.4 : 1,
            }}>
              <span style={{ minWidth: '1rem', color: isVantage ? '#fbbf24' : isCandidate ? '#4ade80' : '#4b5563' }}>
                {isVantage ? '◆' : isCandidate ? '○' : '·'}
              </span>
              <span style={{
                minWidth: '9rem',
                color: isVantage ? '#fbbf24' : isCandidate ? '#e2e8f0' : '#6b7280',
                textDecoration: !isVantage && !isCandidate ? 'line-through' : undefined,
              }}>
                {host.name}
              </span>
              <span style={{ display: 'flex', gap: '0.3rem' }}>
                {isVantage && (live?.pingsLeft ?? 0) > 0 && (
                  <button
                    onClick={() => void send({ kind: 'ping', host: host.id })}
                    disabled={busy}
                    style={{ ...row, fontSize: '0.7rem', padding: '0.05rem 0.4rem' }}
                    title="Report which way traffic to the target leaves here"
                  >
                    ping
                  </button>
                )}
                {pinned === host.id && (
                  <button
                    className="primary"
                    onClick={() => void send({ kind: 'name', host: host.id })}
                    disabled={busy}
                    style={{ ...row, fontSize: '0.7rem', padding: '0.05rem 0.5rem' }}
                    title="Nothing else fits your pings"
                  >
                    it's here
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {/* Reserved, so a refusal never moves what is under it. */}
      <div style={{ height: '1.2rem', marginBottom: '0.3rem', overflow: 'hidden' }}>
        {note && <span style={{ ...row, fontSize: '0.76rem', color: '#fbbf24' }}>{note}</span>}
        {!note && candidates.length > 1 && (live?.pingsLeft ?? 0) === 0 && (
          <span style={{ ...row, fontSize: '0.76rem', color: '#f87171' }}>
            Out of pings with {candidates.length} hosts still fitting — it was never pinned down.
          </span>
        )}
      </div>

      {pinned === null && (
        <button
          onClick={() => onSubmit({})}
          disabled={busy}
          style={{ ...row, fontSize: '0.78rem', padding: '0.25rem 0.7rem' }}
          title="Give up on this sweep"
        >
          Abandon the sweep
        </button>
      )}
    </div>
  );
}
